// src/components/ListasTrabajo/Compartidos/SelectorListas.js

import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    IconButton,
    Radio,
    Typography,
    Box,
    CircularProgress,
    Divider,
    TextField,
    InputAdornment,
    Snackbar,
    Alert as MuiAlert,
} from '@mui/material';
import {
    Folder as FolderIcon,
    Add as AddIcon,
    Search as SearchIcon,
    Close as CloseIcon
} from '@mui/icons-material';

import useListasTrabajo from '../../../hooks/useListasTrabajo';
import CrearListaDialog from '../GestionListas/CrearListaDialog';

const SelectorListas = ({
    open,
    onClose,
    expedientesIds = [],
    tipoExpediente,
    onSuccess,
    onListaSeleccionada
}) => {
    const { listas, loading, agregarExpedientes, cargarListas, crearLista } = useListasTrabajo(tipoExpediente);

    const [listaSeleccionada, setListaSeleccionada] = useState(null);
    const [busqueda, setBusqueda] = useState('');
    const [mostrarCrearLista, setMostrarCrearLista] = useState(false);
    const [agregando, setAgregando] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'warning' });

    useEffect(() => {
        if (open) {
            setListaSeleccionada(null);
            setBusqueda('');
        }
    }, [open]);

    const listasFiltradas = listas.filter(lista =>
        String(lista?.Nombre || '').toLowerCase().includes(busqueda.toLowerCase())
    );

    const handleAgregar = async () => {
        if (!listaSeleccionada) {
            setSnackbar({ open: true, message: 'Selecciona una lista primero', severity: 'warning' });
            return;
        }

        setAgregando(true);
        try {
            await agregarExpedientes(listaSeleccionada, expedientesIds);

            if (onSuccess) {
                onSuccess();
            } else if (onListaSeleccionada) {
                onListaSeleccionada();
            }

            onClose();
        } catch (error) {
            console.error('Error al agregar a lista:', error);
        } finally {
            setAgregando(false);
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { minHeight: 400, maxHeight: 600 } }}
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="h6">
                            Agregar a Lista de Trabajo
                        </Typography>
                        <IconButton size="small" onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        {expedientesIds.length} expediente(s) seleccionado(s)
                    </Typography>
                </DialogTitle>

                <DialogContent sx={{ p: 2 }}>
                    {/* Busqueda */}
                    <TextField
                        fullWidth
                        placeholder="Buscar lista..."
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        sx={{ mb: 2 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            )
                        }}
                    />

                    {/* Boton crear nueva */}
                    <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => setMostrarCrearLista(true)}
                        style={{ marginBottom: 16 }}
                    >
                        Crear nueva lista
                    </Button>

                    <Divider style={{ marginBottom: 16 }} />

                    {/* Lista de listas */}
                    {loading ? (
                        <Box display="flex" justifyContent="center" p={4}>
                            <CircularProgress />
                        </Box>
                    ) : listasFiltradas.length === 0 ? (
                        <Box sx={{ textAlign: 'center', p: 4, color: 'text.secondary' }}>
                            <FolderIcon style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }} />
                            <Typography variant="body1">
                                {listas.length === 0
                                    ? 'No tienes listas creadas'
                                    : 'No se encontraron listas'}
                            </Typography>
                        </Box>
                    ) : (
                        <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                            {listasFiltradas.map((lista) => (
                                <ListItem
                                    key={lista.Id}
                                    component="div"
                                    sx={{
                                        borderRadius: 1,
                                        mb: 1,
                                        '&:hover': {
                                            backgroundColor: 'action.hover'
                                        }
                                    }}
                                    selected={listaSeleccionada === lista.Id}
                                    onClick={() => setListaSeleccionada(lista.Id)}
                                >
                                    <ListItemIcon>
                                        <Radio
                                            checked={listaSeleccionada === lista.Id}
                                            color="primary"
                                        />
                                    </ListItemIcon>
                                    <ListItemIcon>
                                        <FolderIcon style={{ color: lista.Color }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={lista.Nombre}
                                        secondaryTypographyProps={{ component: 'div' }}
                                        secondary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <span>{lista.Total_Expedientes || 0} expedientes</span>
                                                {lista.Descripcion && (
                                                    <>
                                                        <span>•</span>
                                                        <span style={{
                                                            maxWidth: 200,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}>
                                                            {lista.Descripcion}
                                                        </span>
                                                    </>
                                                )}
                                            </Box>
                                        }
                                    />
                                </ListItem>
                            ))}
                        </List>
                    )}
                </DialogContent>

                <DialogActions style={{ padding: 16 }}>
                    <Button onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleAgregar}
                        color="primary"
                        variant="contained"
                        disabled={!listaSeleccionada || agregando}
                        startIcon={agregando && <CircularProgress size={20} color="inherit" />}
                    >
                        {agregando ? 'Agregando...' : 'Agregar a lista'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog crear lista */}
            <CrearListaDialog
                open={mostrarCrearLista}
                onClose={() => setMostrarCrearLista(false)}
                tipoExpediente={tipoExpediente}
                onGuardar={async (datosLista) => {
                    try {
                        setAgregando(true);

                        // 1. Crear la lista
                        const nuevaLista = await crearLista(datosLista);

                        // 2. Recargar listas para tenerlas actualizadas
                        await cargarListas();

                        // 3. Cerrar el modal de crear
                        setMostrarCrearLista(false);

                        // 4. Seleccionar automaticamente la nueva lista
                        setListaSeleccionada(nuevaLista.Id);

                        // 5. Agregar los expedientes a la nueva lista
                        await agregarExpedientes(nuevaLista.Id, expedientesIds);

                        // 6. Cerrar todo y notificar exito
                        onClose();
                        if (onSuccess) {
                            onSuccess();
                        } else if (onListaSeleccionada) {
                            onListaSeleccionada();
                        }
                    } catch (error) {
                        console.error('Error en el flujo de crear lista:', error);
                    } finally {
                        setAgregando(false);
                    }
                }}
            />

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <MuiAlert
                    elevation={6}
                    variant="filled"
                    severity={snackbar.severity}
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                >
                    {snackbar.message}
                </MuiAlert>
            </Snackbar>
        </>
    );
};

export default SelectorListas;